import React, { useState, useEffect, useCallback } from 'react';
import Big from 'big.js';
import { useAutoFix } from '../context/AutoFixContext';
import { targetAppStore } from './store';
import {
  AlertTriangle,
  CheckCircle2,
  ShieldCheck,
  Bug,
  Keyboard,
  RotateCcw,
  Zap,
} from 'lucide-react';

export const CalculatorApp: React.FC = () => {
  const { activeIncident, triggerBug, soundEnabled } = useAutoFix();
  const [total, setTotal] = useState<string | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [operation, setOperation] = useState<string | null>(null);
  const [localCrash, setLocalCrash] = useState<string | null>(null);
  const [lastEquation, setLastEquation] = useState<string | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);

  // Check if patch for calc-div-zero is applied
  const isHealed =
    (activeIncident?.bugId === 'calc-div-zero' &&
      (activeIncident.phase === 'resolved' || activeIncident.autoHealed)) ||
    !!targetAppStore.getState().runtimePatchesApplied['patch-calc-div-zero'];

  const playClickSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(600, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.05);
      gain.gain.setValueAtTime(0.04, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.001, ctx.currentTime + 0.05);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.05);
    } catch {
      // Audio context might be restricted
    }
  }, [soundEnabled]);

  // Handle external reset
  useEffect(() => {
    const handleCalcReset = () => {
      setTotal(null);
      setOperation(null);
      setNext(null);
      setLocalCrash(null);
      setLastEquation(null);
    };

    const handleCalcTrigger = () => {
      setTotal('5');
      setOperation('÷');
      setNext('0');
      setLastEquation('5 ÷ 0 =');
      setLocalCrash('ZeroDivisionError: Cannot divide by zero in operate.js');
    };

    window.addEventListener('calc-reset-fault', handleCalcReset);
    window.addEventListener('calc-trigger-fault', handleCalcTrigger);
    return () => {
      window.removeEventListener('calc-reset-fault', handleCalcReset);
      window.removeEventListener('calc-trigger-fault', handleCalcTrigger);
    };
  }, []);

  // When healed, auto-clear local crash banner
  useEffect(() => {
    if (isHealed) {
      setLocalCrash(null);
    }
  }, [isHealed]);

  const operate = (numberOne: string | null, numberTwo: string | null, op: string): string => {
    try {
      const one = Big(numberOne || '0');
      const two = Big(numberTwo || (op === '÷' || op === 'x' ? '1' : '0'));

      if (op === '+') return one.plus(two).toString();
      if (op === '-') return one.minus(two).toString();
      if (op === 'x') return one.times(two).toString();
      if (op === '÷') {
        if (two.eq(0)) {
          if (isHealed) {
            // Hotpatch safe clamp
            return '0';
          }
          // Real-world crash in ahfarmer/calculator
          const errMsg = 'ZeroDivisionError: Cannot divide by zero in operate.js';
          setLocalCrash(errMsg);
          setLastEquation(`${numberOne || '5'} ÷ 0 =`);
          triggerBug('calc-div-zero', {
            errorMessage: errMsg,
            errorType: 'ZeroDivisionError',
            stackTrace:
              '    at operate (real-world-app/src/logic/operate.js:17:13)\n    at calculate (real-world-app/src/logic/calculate.js:52:14)\n    at handleClick (real-world-app/src/component/App.jsx:15:23)',
            userAction: `Evaluated ${numberOne || '5'} ÷ 0 =`,
          });
          return 'Error';
        }
        return one.div(two).toString();
      }
      return '0';
    } catch {
      return '0';
    }
  };

  const calculate = (obj: any, buttonName: string) => {
    if (buttonName === 'AC') {
      return { total: null, next: null, operation: null, resetEquation: true };
    }

    if (/[0-9]/.test(buttonName)) {
      if (buttonName === '0' && obj.next === '0') return {};
      if (obj.operation) {
        if (obj.next) return { next: obj.next + buttonName };
        return { next: buttonName };
      }
      if (obj.next) {
        const n = obj.next === '0' ? buttonName : obj.next + buttonName;
        return { next: n, total: null };
      }
      return { next: buttonName, total: null };
    }

    if (buttonName === '.') {
      if (obj.next) {
        if (obj.next.includes('.')) return {};
        return { next: obj.next + '.' };
      }
      return { next: '0.' };
    }

    if (buttonName === '=') {
      if (obj.next && obj.operation) {
        const eqStr = `${obj.total || '0'} ${obj.operation} ${obj.next} =`;
        const res = operate(obj.total, obj.next, obj.operation);
        if (res === 'Error') {
          return { total: null, next: null, operation: null, equation: eqStr };
        }
        return {
          total: res,
          next: null,
          operation: null,
          equation: eqStr,
        };
      }
      return {};
    }

    if (buttonName === '+/-') {
      if (obj.next) return { next: (-1 * parseFloat(obj.next)).toString() };
      if (obj.total) return { total: (-1 * parseFloat(obj.total)).toString() };
      return {};
    }

    if (buttonName === '%') {
      if (obj.operation && obj.next) {
        const result = operate(obj.total, obj.next, obj.operation);
        if (result === 'Error') return {};
        return {
          total: Big(result).div(Big('100')).toString(),
          next: null,
          operation: null,
        };
      }
      if (obj.next) return { next: Big(obj.next).div(Big('100')).toString() };
      return {};
    }

    // Default operations (+, -, x, ÷)
    if (obj.operation) {
      if (!obj.next) {
        // Change operator without evaluating
        return { operation: buttonName };
      }
      const res = operate(obj.total, obj.next, obj.operation);
      if (res === 'Error') {
        return { total: null, next: null, operation: null };
      }
      return {
        total: res,
        next: null,
        operation: buttonName,
      };
    }

    if (!obj.next) return { operation: buttonName };

    return {
      total: obj.next,
      next: null,
      operation: buttonName,
    };
  };

  const handleBtnClick = useCallback(
    (btnName: string) => {
      playClickSound();
      setActiveKey(btnName);
      setTimeout(() => setActiveKey(null), 150);

      // If recovering from an error with a new number or AC, clear the error
      if (localCrash && (/[0-9]/.test(btnName) || btnName === 'AC' || btnName === '.')) {
        setLocalCrash(null);
      }

      try {
        const res = calculate({ total, next, operation }, btnName);
        if (res.total !== undefined) setTotal(res.total);
        if (res.next !== undefined) setNext(res.next);
        if (res.operation !== undefined) setOperation(res.operation);
        if (res.equation !== undefined) setLastEquation(res.equation);
        if (res.resetEquation) setLastEquation(null);
        if (btnName === 'AC') {
          setLocalCrash(null);
        }
      } catch (e: any) {
        setLocalCrash(e.message || String(e));
      }
    },
    [total, next, operation, localCrash, playClickSound]
  );

  // Physical Keyboard Listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Avoid capturing when user is typing in an input
      if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) return;

      const key = e.key;
      if (/[0-9]/.test(key)) {
        handleBtnClick(key);
      } else if (key === '+') {
        handleBtnClick('+');
      } else if (key === '-') {
        handleBtnClick('-');
      } else if (key === '*' || key.toLowerCase() === 'x') {
        handleBtnClick('x');
      } else if (key === '/') {
        e.preventDefault();
        handleBtnClick('÷');
      } else if (key === '=' || key === 'Enter') {
        e.preventDefault();
        handleBtnClick('=');
      } else if (key === '.' || key === ',') {
        handleBtnClick('.');
      } else if (key === '%') {
        handleBtnClick('%');
      } else if (key === 'Escape' || key.toLowerCase() === 'c') {
        handleBtnClick('AC');
      } else if (key === 'Backspace') {
        if (next && next.length > 1) {
          setNext(next.slice(0, -1));
        } else {
          setNext(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleBtnClick, next]);

  const handleTriggerCrash = () => {
    setTotal('5');
    setOperation('÷');
    setNext('0');
    setLastEquation('5 ÷ 0 =');
    const errMsg = 'ZeroDivisionError: Cannot divide by zero in operate.js';
    setLocalCrash(errMsg);
    triggerBug('calc-div-zero', {
      errorMessage: errMsg,
      errorType: 'ZeroDivisionError',
      stackTrace:
        '    at operate (real-world-app/src/logic/operate.js:17:13)\n    at calculate (real-world-app/src/logic/calculate.js:52:14)\n    at handleClick (real-world-app/src/component/App.jsx:15:23)',
      userAction: 'Clicked "Trigger 5 ÷ 0 Crash" button',
    });
  };

  const handleRunDemoMath = () => {
    setTotal('96');
    setNext(null);
    setOperation(null);
    setLastEquation('12 × 8 =');
    setLocalCrash(null);
  };

  const buttons = [
    ['AC', '+/-', '%', '÷'],
    ['7', '8', '9', 'x'],
    ['4', '5', '6', '-'],
    ['1', '2', '3', '+'],
    ['0', '.', '='],
  ];

  // Screen display derivation
  const displayValue = localCrash
    ? 'Error'
    : next !== null
    ? next
    : total !== null
    ? total
    : '0';

  const subDisplay =
    total && operation
      ? `${total} ${operation} ${next !== null ? next : ''}`
      : lastEquation
      ? lastEquation
      : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '16px 20px', gap: 14 }}>
      {/* Target App Badge */}
      <div
        style={{
          background: 'rgba(30, 41, 59, 0.8)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 800,
              fontSize: 16,
            }}
          >
            🧮
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>
              Real-World Target: <code>ahfarmer/calculator</code> (2.7k GitHub ⭐)
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>Unmodified open-source React Calculator with live fault injection</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--accent)' }}>
                <Keyboard size={12} /> Physical Keyboard Enabled
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isHealed ? (
            <span
              className="badge badge-green"
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', fontSize: 11 }}
            >
              <ShieldCheck size={14} />
              Runtime Hotpatch Active
            </span>
          ) : (
            <button
              className="btn btn-sm"
              onClick={handleTriggerCrash}
              style={{
                background: 'rgba(239, 68, 68, 0.15)',
                borderColor: 'rgba(239, 68, 68, 0.4)',
                color: '#f87171',
                fontWeight: 700,
                fontSize: 11,
                display: 'flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              <Bug size={13} />
              Trigger 5 ÷ 0 Crash
            </button>
          )}
        </div>
      </div>

      {/* Crash Banner if triggered */}
      {localCrash && !isHealed && (
        <div
          style={{
            background: 'rgba(239, 68, 68, 0.12)',
            border: '1px solid var(--red)',
            borderRadius: 6,
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={16} style={{ color: 'var(--red)' }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#fca5a5' }}>
                Uncaught Exception: {localCrash}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-2)' }}>
                AutoFix is autonomously analyzing AST and synthesizing an in-memory hotpatch...
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Healed Notification Banner */}
      {isHealed && (
        <div
          style={{
            background: 'rgba(16, 185, 129, 0.12)',
            border: '1px solid var(--green)',
            borderRadius: 6,
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <CheckCircle2 size={16} style={{ color: 'var(--green)' }} />
          <div style={{ fontSize: 11, color: '#6ee7b7' }}>
            <strong>Healed via WebMCP Hotpatch:</strong> Division by zero in <code>operate.js</code> is clamped to <code>0</code> in memory without reloading the page. All regression assertions verified!
          </div>
        </div>
      )}

      {/* Calculator Body */}
      <div
        style={{
          maxWidth: 360,
          margin: '0 auto',
          width: '100%',
          background: '#0f172a',
          borderRadius: 14,
          border: '1px solid #334155',
          overflow: 'hidden',
          boxShadow: '0 20px 30px rgba(0, 0, 0, 0.5)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Display Screen */}
        <div
          style={{
            background: '#020617',
            padding: '16px 20px',
            textAlign: 'right',
            color: '#f8fafc',
            fontFamily: 'var(--font-mono)',
            borderBottom: '1px solid #1e293b',
            minHeight: 92,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'center',
            wordBreak: 'break-all',
          }}
        >
          <div style={{ fontSize: 13, color: '#94a3b8', minHeight: 18, marginBottom: 4 }}>
            {subDisplay}
            {isHealed && subDisplay.includes('÷ 0') ? (
              <span style={{ marginLeft: 6, color: '#10b981', fontSize: 11, fontWeight: 600 }}>
                (Clamped by Hotpatch)
              </span>
            ) : null}
          </div>
          <div
            style={{
              fontSize: 34,
              fontWeight: 700,
              color: localCrash ? '#f87171' : '#f8fafc',
              letterSpacing: '-0.5px',
            }}
          >
            {displayValue}
          </div>
        </div>

        {/* Buttons Grid */}
        <div style={{ display: 'flex', flexDirection: 'column', background: '#0f172a' }}>
          {buttons.map((row, rIdx) => (
            <div key={rIdx} style={{ display: 'flex', height: 58, borderBottom: rIdx < 4 ? '1px solid #1e293b' : 'none' }}>
              {row.map((btn) => {
                const isOrange = ['÷', 'x', '-', '+', '='].includes(btn);
                const isZero = btn === '0';
                const isPressed = activeKey === btn;
                return (
                  <button
                    key={btn}
                    onClick={() => handleBtnClick(btn)}
                    style={{
                      flex: isZero ? 2 : 1,
                      border: 'none',
                      borderRight: '1px solid #1e293b',
                      background: isPressed
                        ? isOrange
                          ? '#c2410c'
                          : '#334155'
                        : isOrange
                        ? '#ea580c'
                        : '#1e293b',
                      color: '#ffffff',
                      fontSize: 18,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.1s ease',
                      outline: 'none',
                      fontFamily: 'var(--font-mono)',
                      transform: isPressed ? 'scale(0.96)' : 'none',
                    }}
                    onMouseEnter={(e) => {
                      if (!isPressed) e.currentTarget.style.filter = 'brightness(1.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.filter = 'none';
                    }}
                  >
                    {btn}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* One-Click Demo Control Pills */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button
          className="btn btn-sm"
          onClick={handleTriggerCrash}
          style={{
            background: 'rgba(239, 68, 68, 0.12)',
            borderColor: 'rgba(239, 68, 68, 0.3)',
            color: '#fca5a5',
            fontSize: 11,
            padding: '4px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Bug size={12} />
          <span>Test 5 ÷ 0 = (Trigger Bug)</span>
        </button>

        <button
          className="btn btn-sm"
          onClick={handleRunDemoMath}
          style={{
            background: 'rgba(56, 189, 248, 0.1)',
            borderColor: 'rgba(56, 189, 248, 0.3)',
            color: '#38bdf8',
            fontSize: 11,
            padding: '4px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Zap size={12} />
          <span>Test 12 × 8 = 96 (Normal Math)</span>
        </button>

        <button
          className="btn btn-sm"
          onClick={() => handleBtnClick('AC')}
          style={{
            background: 'rgba(148, 163, 184, 0.1)',
            borderColor: 'rgba(148, 163, 184, 0.3)',
            color: '#94a3b8',
            fontSize: 11,
            padding: '4px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <RotateCcw size={12} />
          <span>Clear (AC)</span>
        </button>
      </div>

      <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-3)' }}>
        Keyboard supported: Type <kbd style={{ background: '#1e293b', padding: '1px 5px', borderRadius: 3 }}>0-9</kbd>, <kbd style={{ background: '#1e293b', padding: '1px 5px', borderRadius: 3 }}>+</kbd>, <kbd style={{ background: '#1e293b', padding: '1px 5px', borderRadius: 3 }}>-</kbd>, <kbd style={{ background: '#1e293b', padding: '1px 5px', borderRadius: 3 }}>*</kbd>, <kbd style={{ background: '#1e293b', padding: '1px 5px', borderRadius: 3 }}>/</kbd>, <kbd style={{ background: '#1e293b', padding: '1px 5px', borderRadius: 3 }}>Enter</kbd>, <kbd style={{ background: '#1e293b', padding: '1px 5px', borderRadius: 3 }}>Esc</kbd>
      </div>
    </div>
  );
};
